/**
 * contextFields - the computed parts of the shell's context values.
 *
 * The context objects themselves stay in the shell beside their dependency
 * lists (see gotchas: a context memo that omits new state serves a stale
 * prompt); what lives here is only the arithmetic inside them, so each memo
 * reads as a list of names. Pure. Extracted from DeathWatchSimulator.
 */

/** The six sheet rolls a panel exposes, from one `useRollActions` record. */
export function rollFields(actions) {
  return {
    rollCharacteristic: actions.rollCharacteristic,
    rollSkill: actions.rollSkill,
    rollPlainTarget: actions.rollPlainTarget,
    rollDiceExpr: actions.rollDiceExpr,
    rollOpposedCharacteristic: actions.rollOpposedCharacteristic,
    rollOpposedSkill: actions.rollOpposedSkill,
  };
}

/**
 * The reaction prompt's fields: WHO owes it, HOW LOUDLY it reads here, HOW
 * LONG they have, the DEFENDER's standing reaction live from the roster
 * (v8.325.0), and what MY attack is waiting on.
 */
export function reactionFields({ reactions, roster, effectivePendingDefence, pendingAttack, onAcceptAll }) {
  const { focused } = reactions;
  const defenderId = effectivePendingDefence?.tabId ?? pendingAttack?.tabId;
  return {
    reactionName: focused?.name ?? null,
    reactionVolume: focused?.volume ?? 'calm',
    standingReaction: roster.find((c) => c.id === defenderId)?.state?.standingReaction ?? null,
    reactionRemainingMs: focused?.remainingMs ?? null,
    reactionsWaiting: reactions.waiting,
    reactionQueue: reactions.queued,
    // Who owes a reaction right now, by id: a squad chip pulses red for one of them (plan map-display, E).
    reactionOwedIds: reactions.owed?.map((r) => r.combatantId) ?? [],
    reactionsAwaiting: reactions.awaiting,
    onAcceptAllReactions: reactions.queued.length > 0 ? onAcceptAll : null,
  };
}

/**
 * The acting character's OWN sheet (v7.1.0): the projection the target panel
 * renders, pointed at whoever you act as, with the same edit controls.
 */
export function actorSheetFields({ actor, actorTarget, actorModHandlers, edits }) {
  return {
    actorTarget,
    actorSourceCharacter: actor?.sheet ?? null,
    actorDefenderMods: actorTarget?.defenderCustomMods ?? [],
    onActorAddMod: actorModHandlers.add,
    onActorEditMod: actorModHandlers.edit,
    onActorRemoveMod: actorModHandlers.remove,
    ...edits,
  };
}

/**
 * The turn tracker as the body map's status band reads it: whose turn it is,
 * and the one thing the band can DO - reroll your own initiative from your
 * own sheet - unless the tracker is locked.
 */
export function trackerFields(turnTracker, isLocked) {
  return {
    turnTrackerState: turnTracker.trackerState,
    rerollInitiative: isLocked('turnTracker') ? null : turnTracker.rerollInitiative,
  };
}

/** The range band's CHOICE as well as its answer: the control shows "Auto" when that is chosen. */
export function rangeFields({ rangeBand, rangeBandChoice, liveMap, setRangeBand }) {
  return {
    rangeBand,
    rangeBandChoice,
    rangeBandFromMap: liveMap.rangeBand,
    mapMetres: liveMap.measurement?.metres ?? null,
    setRangeBand,
  };
}
